/**
 * The action a signed-out reader was taking when they were sent to log in,
 * kept so it can continue once they're signed in.
 *
 * Every kind of login ends the same way: the reader is taken back to the page
 * they left, and that page takes the pending action and resumes it. The action
 * is kept in the tab's session storage, which survives a full-page trip to a
 * login provider and back and doesn't reach other tabs, with an in-memory copy
 * for third-party iframes that block storage.
 */

const PENDING_LOGIN_ACTION_KEY = 'psi-pending-login-action';
const MAX_AGE_MS = 30 * 60 * 1000;
const LOGIN_REDIRECT_PATH = /\/login\/one\/(fragmentRedirect|codeRedirect)\/?$/;

export type LoginDestination = {
    structureKey: string;
    instanceKey: string;
    screenKey?: string;
    params: Record<string, unknown>;
};

/**
 * An action a component on the page runs itself once the reader is back,
 * named by `key` so the component can tell it is the one to run it.
 */
export type LoginResume = {
    key: string;
    params: Record<string, unknown>;
};

/**
 * - `returnUrl` is the page to return to after a login that reloaded the app.
 * - `structureKey` and `instanceKey` name that page's instance, which resumes
 *   the action.
 * - `destination`, when set, is the screen the reader was heading for.
 * - `resume`, when set, is the action a component on the page runs.
 * - `awaitingRedirect` is set while the tab is away at a full-page login
 *   provider, and cleared once the provider's redirect signs the reader in.
 */
export type PendingLoginAction = {
    returnUrl: string | null;
    structureKey: string;
    instanceKey: string;
    destination?: LoginDestination;
    resume?: LoginResume;
    awaitingRedirect?: boolean;
    createdAt: number;
};

let inMemoryAction: PendingLoginAction | null = null;

export function putPendingLoginAction(action: Omit<PendingLoginAction, 'createdAt'>) {
    storeAction({ ...action, createdAt: Date.now() });
}

/** Note that the tab is about to leave for a full-page login provider. */
export function markPendingLoginActionAwaitingRedirect() {
    setAwaitingRedirect(true);
}

/** Note that a full-page login provider's redirect signed the reader in. */
export function markPendingLoginActionRedirected() {
    setAwaitingRedirect(false);
}

/**
 * Drops an action left waiting on a full-page login the reader abandoned,
 * such as by pressing the browser's back button at the provider. Runs as the
 * app loads: the provider's redirect lands on a login redirect route, so a
 * load anywhere else means the login never finished.
 */
export function dropAbandonedPendingLoginAction() {
    if (!getPendingLoginAction()?.awaitingRedirect) return;
    if (!LOGIN_REDIRECT_PATH.test(readPathname())) {
        clearPendingLoginAction();
    }
}

/** The pending action, or `null` if there is none or it has expired. */
export function getPendingLoginAction(): PendingLoginAction | null {
    const action = inMemoryAction ?? readStoredAction();
    if (!action) return null;
    if (Date.now() - action.createdAt > MAX_AGE_MS) {
        clearPendingLoginAction();
        return null;
    }
    return action;
}

export function clearPendingLoginAction() {
    inMemoryAction = null;
    try {
        window.sessionStorage.removeItem(PENDING_LOGIN_ACTION_KEY);
    } catch {
        // Some third-party iframes block session storage.
    }
}

function setAwaitingRedirect(awaitingRedirect: boolean) {
    const action = getPendingLoginAction();
    if (action) storeAction({ ...action, awaitingRedirect });
}

function storeAction(action: PendingLoginAction) {
    inMemoryAction = action;
    try {
        window.sessionStorage.setItem(PENDING_LOGIN_ACTION_KEY, JSON.stringify(action));
    } catch {
        // Some third-party iframes block session storage.
    }
}

function readPathname(): string {
    try {
        return window.location.pathname;
    } catch {
        return '';
    }
}

function readStoredAction(): PendingLoginAction | null {
    try {
        const stored = window.sessionStorage.getItem(PENDING_LOGIN_ACTION_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
}
